import React, { useCallback, useMemo, useState } from 'react';
import Modal from 'react-modal';

import { useAppSelector } from '../../../hooks/react-redux';
import { DocumentPreviewViewer } from '../../documents/document-viewer/DocumentPreviewViewer';
import { Button } from '../../shared/button/Button';
import { PageLoadingDots } from '../../shared/loading-dots/PageLoadingDots';
import styles from './Opinions.module.scss';
import { ArkOpinion, getIsDownloadingSummary, getIsOpeningSummary, getOpinion, getOpinionSummaryUrl } from './store';
import { RadioRow } from '../../shared/radio/RadioRow';
import { RadioListOption } from '../../shared/radio/RadioList';
import { DocumentViewerType } from '../../documents/document-viewer/store';
import { MimeType } from '../../constants/MimeType';

interface SummaryPreviewModalProps {
    isOpen: boolean;
    closeModal: () => void;
    download: (opinion: ArkOpinion, asDocX: boolean) => void;
    canDownload: boolean;
    hasDocXSummary: boolean;
    hasPdfSummary: boolean;
    internetGatewayOpen: boolean;
}

export const SummaryPreviewModal: React.FC<SummaryPreviewModalProps> = ({ isOpen, closeModal, download, canDownload, hasDocXSummary, hasPdfSummary, internetGatewayOpen }) => {
    const [asDocX, setAsDocX] = useState<boolean>(!hasPdfSummary);
    const isOpeningSummary = useAppSelector(getIsOpeningSummary);
    const isDownloadingSummary = useAppSelector(getIsDownloadingSummary);
    const previewUrl = useAppSelector(getOpinionSummaryUrl);
    const opinion = useAppSelector(getOpinion);
    const downloadSummary = useCallback(() => opinion && download(opinion, asDocX), [opinion, download, asDocX]);

    const mimeTypeOptions: RadioListOption[] = useMemo(() => [
        {
            id: MimeType.PDF,
            isSelected: !asDocX,
            label: 'PDF',
            disabled: !hasDocXSummary || !hasPdfSummary
        },
        {
            id: MimeType.DOCX,
            isSelected: asDocX,
            label: 'DocX (Word Doc)',
            disabled: !hasDocXSummary || !hasPdfSummary
        }
    ], [asDocX, hasDocXSummary, hasPdfSummary]);

    const setMimeType = useCallback((id: string) => setAsDocX(id === MimeType.DOCX), [setAsDocX]);

    const downloadLabel = useMemo(() => `Download ${asDocX ? 'DocX' : 'PDF'}`, [asDocX]);

    return (
        <Modal
            isOpen={isOpen}
            className={styles.summaryPreviewModal}
            ariaHideApp={false}
            style={{ overlay: { display: 'flex', zIndex: 20 } }}
            shouldCloseOnOverlayClick={!isOpeningSummary}
            shouldCloseOnEsc={!isOpeningSummary}
            onRequestClose={closeModal}
        >
            <div className={styles.summaryPreviewWrapper}>
                <div className={styles.summaryPreview}>
                    {isOpeningSummary ? <PageLoadingDots label='Opening Document' /> : (
                        <DocumentPreviewViewer url={previewUrl} mimeType={MimeType.PDF} type={DocumentViewerType.OPINION_SUMMARY_PREVIEW} />
                    )}
                </div>
                <div className={styles.footerWrapper}>
                    <div className={styles.mimeTypeSelectWrapper}>
                        <div className={styles.mimeTypeSelectLabel}>Download as: </div>
                        <div className={styles.mimeTypeSelectOptions}>
                            <RadioRow options={mimeTypeOptions} onChange={setMimeType} testId='opinion-summary-download-mime-type' />
                        </div>
                    </div>
                    <div className={styles.buttonWrapper}>
                        <Button onClick={closeModal} label='Close' disabled={isOpeningSummary} testId='opinion-summary-preview-close' />
                        <Button onClick={downloadSummary} label={downloadLabel} disabled={isOpeningSummary || isDownloadingSummary || !canDownload || internetGatewayOpen} testId='opinion-summary-preview-download' />
                    </div>
                </div>
            </div>
        </Modal>
    );
};
